import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Stack,
  Divider,
  Link as MuiLink,
  useTheme,
  alpha,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PointOfSale as PosIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Article content data
const articlesData = {
  'pos-system-transform-business': {
    title: '10 Ways a POS System Can Transform Your Business',
    category: 'Business Growth',
    readTime: '5 min read',
    publishDate: 'January 15, 2026',
    content: [
      {
        type: 'paragraph',
        text: 'In today\'s competitive business landscape, having the right tools can make all the difference between thriving and merely surviving. A modern Point of Sale (POS) system is no longer just a cash register—it\'s a comprehensive business management solution that can revolutionize how you operate.'
      },
      {
        type: 'heading',
        text: '1. Streamlined Checkout Process'
      },
      {
        type: 'paragraph',
        text: 'A modern POS system dramatically speeds up the checkout process. With barcode scanning, quick product search, and multiple payment options, you can serve more customers in less time. This improved efficiency leads to shorter queues, happier customers, and increased sales potential.'
      },
      {
        type: 'heading',
        text: '2. Real-Time Inventory Tracking'
      },
      {
        type: 'paragraph',
        text: 'Gone are the days of manual inventory counts and spreadsheets. A POS system automatically updates your inventory with every sale, giving you real-time visibility into stock levels. Set up automatic alerts for low stock items and never miss a sale due to stockouts again.'
      },
      {
        type: 'heading',
        text: '3. Detailed Sales Analytics'
      },
      {
        type: 'paragraph',
        text: 'Understanding your sales patterns is crucial for business growth. Modern POS systems provide detailed analytics on your best-selling products, peak sales hours, and seasonal trends. Use this data to make informed decisions about inventory, staffing, and marketing.'
      },
      {
        type: 'heading',
        text: '4. Employee Management'
      },
      {
        type: 'paragraph',
        text: 'Track employee performance, manage schedules, and control access levels all from your POS system. See which team members are your top performers and identify areas where additional training might be needed.'
      },
      {
        type: 'heading',
        text: '5. Multi-Location Management'
      },
      {
        type: 'paragraph',
        text: 'If you have multiple store locations, a cloud-based POS system allows you to manage all of them from a single dashboard. Compare performance across locations, transfer inventory between stores, and maintain consistent pricing and promotions.'
      },
      {
        type: 'heading',
        text: '6. Customer Relationship Management'
      },
      {
        type: 'paragraph',
        text: 'Build stronger relationships with your customers by tracking their purchase history and preferences. Implement loyalty programs, send personalized promotions, and provide a more personalized shopping experience.'
      },
      {
        type: 'heading',
        text: '7. Reduced Human Error'
      },
      {
        type: 'paragraph',
        text: 'Manual processes are prone to errors. A POS system automates calculations, applies discounts correctly, and ensures accurate pricing. This reduces costly mistakes and improves your bottom line.'
      },
      {
        type: 'heading',
        text: '8. Improved Cash Flow Management'
      },
      {
        type: 'paragraph',
        text: 'With detailed financial reporting and real-time sales data, you can better manage your cash flow. Track daily revenues, monitor expenses, and make smarter financial decisions for your business.'
      },
      {
        type: 'heading',
        text: '9. Enhanced Security'
      },
      {
        type: 'paragraph',
        text: 'Modern POS systems come with robust security features including encrypted transactions, user access controls, and detailed audit trails. Protect your business and your customers\' data with enterprise-grade security.'
      },
      {
        type: 'heading',
        text: '10. Scalability for Growth'
      },
      {
        type: 'paragraph',
        text: 'As your business grows, your POS system grows with you. Add new products, open new locations, and onboard new employees without missing a beat. A good POS system is an investment in your future success.'
      },
      {
        type: 'paragraph',
        text: 'Ready to transform your business? Flex PoS offers all these features and more, designed specifically for businesses like yours. Start your free trial today and experience the difference a modern POS system can make.'
      }
    ]
  },
  'efficient-inventory-management': {
    title: 'The Complete Guide to Efficient Inventory Management',
    category: 'Operations',
    readTime: '7 min read',
    publishDate: 'January 22, 2026',
    content: [
      {
        type: 'paragraph',
        text: 'Effective inventory management is the backbone of any successful retail or hospitality business. Too much inventory ties up capital and risks spoilage; too little means lost sales and disappointed customers. Finding the right balance is both an art and a science.'
      },
      {
        type: 'heading',
        text: 'Understanding Inventory Costs'
      },
      {
        type: 'paragraph',
        text: 'Before diving into strategies, it\'s important to understand the true cost of inventory. Beyond the purchase price, you\'re paying for storage space, insurance, potential spoilage or obsolescence, and the opportunity cost of tied-up capital. Studies show that carrying costs can add 20-30% to the value of inventory annually.'
      },
      {
        type: 'heading',
        text: 'The ABC Analysis Method'
      },
      {
        type: 'paragraph',
        text: 'Not all inventory is created equal. The ABC analysis categorizes your products into three groups: A items (high-value, low-quantity), B items (moderate value and quantity), and C items (low-value, high-quantity). Focus your attention and resources on A items while implementing simpler controls for C items.'
      },
      {
        type: 'heading',
        text: 'Setting Reorder Points'
      },
      {
        type: 'paragraph',
        text: 'A reorder point is the inventory level at which you should place a new order. Calculate this by considering your average daily sales, lead time from suppliers, and desired safety stock. Modern POS systems can automate this calculation and alert you when it\'s time to reorder.'
      },
      {
        type: 'heading',
        text: 'First-In, First-Out (FIFO)'
      },
      {
        type: 'paragraph',
        text: 'Especially important for perishable goods, FIFO ensures that older inventory is sold first. This reduces waste, maintains product quality, and keeps your customers happy with fresh products. Organize your storage to make FIFO easy to implement.'
      },
      {
        type: 'heading',
        text: 'Regular Stock Audits'
      },
      {
        type: 'paragraph',
        text: 'Even with the best systems, physical counts are essential. Schedule regular audits—whether daily cycle counts for high-value items or quarterly full inventories. Compare physical counts to system records to identify and address discrepancies.'
      },
      {
        type: 'heading',
        text: 'Leveraging Technology'
      },
      {
        type: 'paragraph',
        text: 'Modern inventory management relies heavily on technology. Barcode scanners speed up receiving and counting. Cloud-based systems provide real-time visibility across locations. Automated alerts prevent stockouts before they happen. Invest in the right tools to manage inventory efficiently.'
      },
      {
        type: 'heading',
        text: 'Supplier Relationships'
      },
      {
        type: 'paragraph',
        text: 'Strong supplier relationships can improve your inventory management. Negotiate better lead times, explore consignment options, and communicate your needs clearly. Good suppliers become partners in your success.'
      },
      {
        type: 'heading',
        text: 'Demand Forecasting'
      },
      {
        type: 'paragraph',
        text: 'Use historical sales data to predict future demand. Account for seasonality, promotions, and market trends. Better forecasting means better purchasing decisions and optimized inventory levels.'
      },
      {
        type: 'heading',
        text: 'Handling Dead Stock'
      },
      {
        type: 'paragraph',
        text: 'Every business accumulates slow-moving or obsolete inventory. Address this proactively through clearance sales, bundling with popular items, or donations. Don\'t let dead stock consume valuable shelf space and capital.'
      },
      {
        type: 'paragraph',
        text: 'Efficient inventory management is a continuous process of analysis, adjustment, and improvement. With the right strategies and tools like Flex PoS, you can optimize your inventory, reduce costs, and improve customer satisfaction.'
      }
    ]
  },
  'data-driven-sales-decisions': {
    title: 'How to Increase Sales with Data-Driven Decisions',
    category: 'Analytics',
    readTime: '6 min read',
    publishDate: 'January 29, 2026',
    content: [
      {
        type: 'paragraph',
        text: 'In the age of information, successful businesses don\'t just collect data—they use it to drive decisions. Your POS system generates valuable insights every day. The question is: are you using this data to its full potential?'
      },
      {
        type: 'heading',
        text: 'Understanding Your Sales Data'
      },
      {
        type: 'paragraph',
        text: 'Every transaction tells a story. Beyond simple revenue figures, your sales data reveals patterns in customer behavior, product performance, and operational efficiency. Start by identifying what metrics matter most to your business goals.'
      },
      {
        type: 'heading',
        text: 'Key Performance Indicators (KPIs)'
      },
      {
        type: 'paragraph',
        text: 'Focus on KPIs that drive action. Average transaction value shows upselling opportunities. Sales per hour indicates staffing needs. Conversion rate reveals how well you\'re turning browsers into buyers. Track these metrics over time to identify trends and measure improvement.'
      },
      {
        type: 'heading',
        text: 'Product Performance Analysis'
      },
      {
        type: 'paragraph',
        text: 'Not all products contribute equally to your success. Identify your top performers and understand why they sell well. Equally important, analyze underperformers to decide whether to improve marketing, adjust pricing, or discontinue them entirely.'
      },
      {
        type: 'heading',
        text: 'Peak Hours and Staffing'
      },
      {
        type: 'paragraph',
        text: 'Analyze sales by hour and day of week to optimize staffing. Ensure you have adequate coverage during busy periods and avoid overstaffing during slow times. This improves customer service while controlling labor costs.'
      },
      {
        type: 'heading',
        text: 'Customer Segmentation'
      },
      {
        type: 'paragraph',
        text: 'Use purchase history to segment your customers. Identify your most valuable customers and what drives their loyalty. Create targeted promotions for different segments to maximize relevance and response rates.'
      },
      {
        type: 'heading',
        text: 'Pricing Optimization'
      },
      {
        type: 'paragraph',
        text: 'Test different price points and analyze the results. Sometimes a small price increase has no impact on volume but significantly improves margins. Data takes the guesswork out of pricing decisions.'
      },
      {
        type: 'heading',
        text: 'Inventory Velocity'
      },
      {
        type: 'paragraph',
        text: 'Track how quickly products sell through. High-velocity items might warrant larger orders or prominent placement. Slow movers might need promotion or markdown. Match your inventory investment to actual demand.'
      },
      {
        type: 'heading',
        text: 'Seasonal Trends'
      },
      {
        type: 'paragraph',
        text: 'Look at year-over-year data to identify seasonal patterns. Prepare for busy seasons with adequate inventory and staff. Plan promotions during historically slow periods to boost sales.'
      },
      {
        type: 'heading',
        text: 'A/B Testing'
      },
      {
        type: 'paragraph',
        text: 'Don\'t guess—test. Try different displays, promotions, or processes and measure the results. Data-driven experimentation leads to continuous improvement.'
      },
      {
        type: 'heading',
        text: 'Creating a Data Culture'
      },
      {
        type: 'paragraph',
        text: 'Share relevant metrics with your team. When everyone understands how their actions impact results, they\'re more engaged and effective. Celebrate wins and use setbacks as learning opportunities.'
      },
      {
        type: 'paragraph',
        text: 'With Flex PoS, you have access to powerful analytics tools that make data-driven decision making easy. From real-time dashboards to detailed reports, you\'ll have the insights you need to grow your business intelligently.'
      }
    ]
  }
};

const ArticlePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { slug } = useParams();

  const article = articlesData[slug];

  if (!article) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>Article Not Found</Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            The article you're looking for doesn't exist.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article.title} | Flex PoS Blog</title>
        <meta name="description" content={article.content[0].text.substring(0, 160)} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.content[0].text.substring(0, 160)} />
        <meta property="og:type" content="article" />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Navigation */}
        <Box
          component="nav"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(8px)',
            backgroundColor: alpha(theme.palette.background.paper, 0.95)
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
              {/* Logo */}
              <Box
                component={Link}
                to="/"
                sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                  }}
                >
                  <PosIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Flex PoS
                </Typography>
              </Box>

              {/* Nav Links */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Button component={Link} to="/login" sx={{ fontWeight: 600 }}>
                  Sign In
                </Button>
                <Button
                  component={Link}
                  to="/register"
                  variant="contained"
                  sx={{
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                    }
                  }}
                >
                  Get Started
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>

        {/* Article Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)',
            color: 'white',
            py: { xs: 6, md: 8 }
          }}
        >
          <Container maxWidth="md">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{
                color: 'white',
                mb: 3,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Back to Home
            </Button>
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Chip
                label={article.category}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {article.readTime}
                </Typography>
              </Box>
            </Stack>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.75rem' },
                lineHeight: 1.2,
                mb: 2
              }}
            >
              {article.title}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Published on {article.publishDate}
            </Typography>
          </Container>
        </Box>

        {/* Article Content */}
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
          <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
            {article.content.map((block, index) => {
              if (block.type === 'heading') {
                return (
                  <Typography
                    key={index}
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mt: index === 0 ? 0 : 4,
                      mb: 2,
                      color: 'text.primary'
                    }}
                  >
                    {block.text}
                  </Typography>
                );
              }
              return (
                <Typography
                  key={index}
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.8,
                    color: 'text.secondary'
                  }}
                >
                  {block.text}
                </Typography>
              );
            })}

            <Divider sx={{ my: 4 }} />

            {/* CTA Section */}
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Ready to transform your business?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Start your free trial today and see the difference Flex PoS can make.
              </Typography>
              <Button
                component={Link}
                to="/register"
                variant="contained"
                size="large"
                sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                  }
                }}
              >
                Start Free Trial
              </Button>
            </Box>
          </Paper>
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            bgcolor: '#0F172A',
            color: 'white',
            py: 4
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5
                  }}
                >
                  <PosIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography variant="body2" sx={{ color: 'grey.500' }}>
                  © {new Date().getFullYear()} Flex PoS. All rights reserved.
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'grey.500' }}>
                Developed by{' '}
                <MuiLink
                  href="https://www.kaziflex.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'primary.light', fontWeight: 600, textDecoration: 'none' }}
                >
                  Kazi Flex LLC
                </MuiLink>
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default ArticlePage;
